import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { usersService, User } from '../services/usersService';
import { useNotification } from '../hooks/useNotification';
import { useUserStore } from '../stores/useUserStore';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';

// Lucide React icons
import { UserPlus, Save, AlertTriangle, Edit } from 'lucide-react';

interface StaffCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user?: User | null; // If provided, dialog is in edit mode
}

// Available roles that can be assigned to staff (excluding high-level roles)
const AVAILABLE_STAFF_ROLES = [
  { value: 'staff', label: 'Staff' },
  { value: 'cashier', label: 'Cashier' },
  { value: 'inventoryClerk', label: 'Inventory Clerk' },
  { value: 'storeManager', label: 'Store Manager' },
];

export const ModernStaffCreateDialog: React.FC<StaffCreateDialogProps> = ({
  open,
  onClose,
  onSuccess,
  user,
}) => {
  const { translate } = useTranslation();
  const { notify } = useNotification();
  const { user: currentUser } = useUserStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isEditMode = !!user;

  const [formData, setFormData] = useState({
    email: '',
    username: '',
    firstName: '',
    lastName: '',
    password: '',
    confirmPassword: '',
    role: 'staff',
  });

  // Helper function to generate username from email
  const usernameFromEmail = (email: string): string => {
    if (!email) return '';
    const username = email.split('@')[0];
    // Remove any non-alphanumeric characters
    return username.replace(/\W/g, '');
  };

  // Reset form when dialog opens/closes or user changes
  useEffect(() => {
    if (open) {
      if (user) {
        // Edit mode: populate form with user data
        const relevantRoles = (user.roles || []).filter((role) =>
          AVAILABLE_STAFF_ROLES.some((r) => r.value === role)
        );
        setFormData({
          email: user.email || '',
          username: user.username || usernameFromEmail(user.email || ''),
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          password: '', // Don't populate password in edit mode
          confirmPassword: '',
          role: relevantRoles[0] || 'staff',
        });
      } else {
        // Create mode: reset form
        setFormData({
          email: '',
          username: '',
          firstName: '',
          lastName: '',
          password: '',
          confirmPassword: '',
          role: 'staff',
        });
      }
      setError(null);
    }
  }, [open, user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.email || !formData.firstName || !formData.lastName) {
      setError('Please fill in all required fields');
      return;
    }

    // Password validation (only required for create mode, optional for edit)
    if (!isEditMode) {
      if (!formData.password || formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    } else if (formData.password) {
      // If password is provided in edit mode, validate it
      if (formData.password.length < 8) {
        setError('Password must be at least 8 characters long');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('Passwords do not match');
        return;
      }
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const userData: Partial<User> = {
        email: formData.email,
        username: formData.username || usernameFromEmail(formData.email),
        firstName: formData.firstName,
        lastName: formData.lastName,
        roles: [formData.role],
      };

      // Only include password if provided (for edit mode) or required (for create mode)
      if (formData.password) {
        (userData as any).password = formData.password; // The service will hash this
      }

      if (isEditMode && user?.id) {
        // Update existing user
        await usersService.updateUser(user.id, userData);
        notify('Staff account updated successfully', { type: 'success' });
      } else {
        // Create new user
        userData.isActive = true;
        await usersService.createUser(userData);
        notify('Staff account created successfully', { type: 'success' });
      }

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : isEditMode
          ? translate(
              'pos.staff.update_error',
              'Failed to update staff account'
            )
          : translate(
              'pos.staff.create_error',
              'Failed to create staff account'
            );
      setError(errorMessage);
      console.error(
        `Error ${isEditMode ? 'updating' : 'creating'} staff account:`,
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Auto-generate username from email when email changes
      if (name === 'email' && !isEditMode) {
        updated.username = usernameFromEmail(value);
      }
      return updated;
    });
  };

  // Determine which roles the current user can assign
  const getAvailableRoles = () => {
    const userRoles = currentUser?.roles || [];
    const viewerRoleLevel = Math.max(
      ...userRoles.map((role) => {
        const levels: Record<string, number> = {
          sysAdmin: 5,
          admin: 4,
          orgAdmin: 4,
          storeManager: 3,
          staff: 2,
          cashier: 1,
        };
        return levels[role] || 0;
      })
    );

    // Users can only assign roles lower than their own level
    return AVAILABLE_STAFF_ROLES.filter((role) => {
      const roleLevels: Record<string, number> = {
        storeManager: 3,
        staff: 2,
        cashier: 1,
        inventoryClerk: 2,
      };
      const roleLevel = roleLevels[role.value] || 0;
      return roleLevel < viewerRoleLevel;
    });
  };

  const availableRoles = getAvailableRoles();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center space-x-2">
            {isEditMode ? (
              <Edit className="h-5 w-5" />
            ) : (
              <UserPlus className="h-5 w-5" />
            )}
            <span>
              {isEditMode
                ? translate('pos.common.action.edit', 'Edit Staff Account')
                : translate('pos.staff.create_title', 'Create Staff Account')}
            </span>
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? translate(
                  'pos.staff.edit_description',
                  'Update staff account information. Leave password fields empty to keep the current password.'
                )
              : translate(
                  'pos.staff.create_description',
                  'Create a new staff account. The staff member will be able to log in with the provided credentials.'
                )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-4 py-4">
            {error && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">
                  {translate('pos.users.fields.firstName', 'First Name')}{' '}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName">
                  {translate('pos.users.fields.lastName', 'Last Name')}{' '}
                  <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">
                {translate('pos.users.fields.email', 'Email')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading || isEditMode} // Email cannot be changed in edit mode
                placeholder="staff@example.com"
              />
              {isEditMode && (
                <p className="text-xs text-muted-foreground">
                  Email cannot be changed
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">
                {translate('pos.users.fields.username', 'Username')}
                {!isEditMode && (
                  <span className="text-muted-foreground text-xs ml-1">
                    (auto-generated)
                  </span>
                )}
              </Label>
              <Input
                id="username"
                name="username"
                type="text"
                value={formData.username}
                onChange={handleChange}
                disabled={loading || !isEditMode} // Auto-generated in create mode, editable in edit mode
                placeholder="Auto-generated from email"
              />
              {!isEditMode && (
                <p className="text-xs text-muted-foreground">
                  Username is automatically generated from email
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">
                {translate('pos.users.fields.roles', 'Role')}{' '}
                <span className="text-destructive">*</span>
              </Label>
              <Select
                value={formData.role}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, role: value }))
                }
                disabled={loading}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  {availableRoles.map((role) => (
                    <SelectItem key={role.value} value={role.value}>
                      {role.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password">
                  {translate('pos.users.fields.password', 'Password')}
                  {!isEditMode && <span className="text-destructive">*</span>}
                  {isEditMode && (
                    <span className="text-muted-foreground text-xs ml-1">
                      (leave empty to keep current)
                    </span>
                  )}
                </Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required={!isEditMode}
                  disabled={loading}
                  placeholder={
                    isEditMode
                      ? 'Leave empty to keep current'
                      : 'At least 8 characters'
                  }
                  minLength={isEditMode ? undefined : 8}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">
                  {translate(
                    'pos.users.fields.confirmPassword',
                    'Confirm Password'
                  )}
                  {!isEditMode && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required={!isEditMode}
                  disabled={loading}
                  placeholder="Confirm password"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
            >
              {translate('pos.common.action.cancel', 'Cancel')}
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? (
                <>
                  <span className="mr-2">...</span>
                  {isEditMode
                    ? translate('pos.common.action.saving', 'Saving...')
                    : translate('pos.common.action.saving', 'Creating...')}
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {isEditMode
                    ? translate('pos.common.action.save', 'Save')
                    : translate('pos.common.action.create', 'Create')}
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
